import { useEffect, useState, useCallback } from 'react';
import {
  History,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import {
  pushAPI,
  type PushNotification,
  type PushNotificationType,
  type PushNotificationStatus,
} from '../../services/api';

const TYPE_LABEL: Record<PushNotificationType, string> = {
  fatura: 'Fatura',
  suporte: 'Suporte',
  promocao: 'Promoção',
  manutencao: 'Manutenção',
  sistema: 'Sistema',
  custom: 'Custom',
};

const STATUS_STYLE: Record<PushNotificationStatus, string> = {
  pending: 'bg-warning-soft/30 text-warning border border-warning-line/50',
  sent: 'bg-primary-soft/30 text-primary border border-primary-line/50',
  delivered: 'bg-success-soft/30 text-success border border-success-line/50',
  failed: 'bg-danger-soft/30 text-danger border border-danger-line/50',
};

const STATUS_LABEL: Record<PushNotificationStatus, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  delivered: 'Entregue',
  failed: 'Falhou',
};

export default function HistoricoTab() {
  const [items, setItems] = useState<PushNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [userId, setUserId] = useState('');
  const [type, setType] = useState<PushNotificationType | ''>('');
  const [status, setStatus] = useState<PushNotificationStatus | ''>('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await pushAPI.listNotifications({
        page,
        limit: 20,
        userId: userId.trim() || undefined,
        type: (type || undefined) as PushNotificationType | undefined,
        status: (status || undefined) as PushNotificationStatus | undefined,
      });
      setItems(r.data);
      setTotalPages(r.pagination.pages || 1);
      setTotal(r.pagination.total);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message ?? e.message ?? 'Erro ao carregar histórico.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, userId, type, status]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset page quando filtros mudam
  useEffect(() => {
    setPage(1);
  }, [userId, type, status]);

  // Filtro client-side por search (título/body)
  const filtered = search.trim()
    ? items.filter((n) => {
        const s = search.toLowerCase();
        return (
          n.title.toLowerCase().includes(s) ||
          n.body.toLowerCase().includes(s) ||
          n.userId.toLowerCase().includes(s)
        );
      })
    : items;

  function fmt(iso?: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return (
      d.toLocaleDateString('pt-BR') +
      ' ' +
      d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    );
  }

  return (
    <div className="bg-surface border border-line rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-fg flex items-center gap-2">
            <History size={18} className="text-primary" />
            Histórico de envios
          </h2>
          <p className="text-sm text-fg-2 mt-1">
            {total} notificação(ões) registrada(s) no total
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-hover hover:bg-active disabled:opacity-50 text-fg rounded-lg text-sm transition"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <div className="relative md:col-span-2">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar título, mensagem ou CPF..."
            className="w-full pl-9 pr-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
          />
        </div>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as PushNotificationType | '')}
          className="px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm focus:border-primary focus:outline-none"
        >
          <option value="">Todos os tipos</option>
          {(Object.keys(TYPE_LABEL) as PushNotificationType[]).map((t) => (
            <option key={t} value={t}>
              {TYPE_LABEL[t]}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as PushNotificationStatus | '')}
          className="px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm focus:border-primary focus:outline-none"
        >
          <option value="">Todos os status</option>
          {(Object.keys(STATUS_LABEL) as PushNotificationStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="Filtrar por CPF/CNPJ (userId exato)..."
          className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
        />
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 mb-4 bg-danger-soft/20 border border-danger-line/50 rounded-lg text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Tabela */}
      <div className="border border-line rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-canvas">
            <tr className="text-left text-xs text-muted uppercase tracking-wide">
              <th className="px-3 py-3">Data</th>
              <th className="px-3 py-3">Destinatário</th>
              <th className="px-3 py-3">Título</th>
              <th className="px-3 py-3">Tipo</th>
              <th className="px-3 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {loading && (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-muted">
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-muted">
                  Nenhuma notificação encontrada.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((n) => (
                <tr key={n.id} className="hover:bg-canvas/50">
                  <td className="px-3 py-3 text-fg-2 text-xs">{fmt(n.createdAt)}</td>
                  <td className="px-3 py-3 text-fg-2 font-mono text-xs">{n.userId}</td>
                  <td className="px-3 py-3 text-fg">
                    <div className="font-medium">{n.title}</div>
                    <div className="text-xs text-muted truncate max-w-md">{n.body}</div>
                  </td>
                  <td className="px-3 py-3 text-fg-2 text-xs">{TYPE_LABEL[n.type]}</td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs ${
                        STATUS_STYLE[n.status]
                      }`}
                    >
                      {STATUS_LABEL[n.status]}
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-muted">
            Página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-2 bg-hover hover:bg-active disabled:opacity-50 text-fg rounded-lg transition"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="p-2 bg-hover hover:bg-active disabled:opacity-50 text-fg rounded-lg transition"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
